import { formatAddressUS, AddressFormatType, isNullish, getWaypointLetter } from '@caresend/utils';
import dayjs from 'dayjs';
import { defineComponent, computed, ref } from 'vue';
import __vue_component__$1 from '../../../../../buttons/ButtonComponent/ButtonComponent.vue.js';
import __vue_component__$4 from './ItineraryWaypointPopover.vue.js';
import __vue_component__ from '../../Workflow/components/shared/AdditionalLocationInstructionsModal.vue.js';
import { usePortalStore } from '../../../store/portal/index.js';
import { setWaypointTotalDuration } from '../../../store/portal/set/index.js';
import { waypointStartTimes } from '../../../store/portal/set/fix.js';
import __vue_component__$2 from '../../../../../ui/Icon.vue.js';
import __vue_component__$3 from '../../../../../ui/ImageComponent.vue.js';
import __vue_component__$5 from '../../../../../ui/Tooltip/Tooltip.vue.js';
import '@caresend/types';
import '@sentry/vue';
import '@fortawesome/fontawesome-svg-core';
import '@fortawesome/sharp-regular-svg-icons';
import 'buefy';
import '../../../../../../functions/configuration/icons.js';
import '../../../../../../database/algoliaConfig/indices.js';
import { preventParentClickAndDefault } from '../../../../../../functions/preventDefault.js';
import 'mobile-device-detect';
import '../../../../../../functions/tracking/index.js';
import { getStore } from '../../../../../../store/helpers/initStore.js';

var script = defineComponent({
    name: 'ItineraryWaypointLocationAndDurationCard',
    components: {
        AdditionalLocationInstructionsModal: __vue_component__,
        ButtonComponent: __vue_component__$1,
        Icon: __vue_component__$2,
        ImageComponent: __vue_component__$3,
        ItineraryWaypointPopover: __vue_component__$4,
        Tooltip: __vue_component__$5,
    },
    props: {
        waypoint: { type: Object, default: () => null },
        inputDisabled: { type: Boolean },
        waypointIndex: { type: Number, default: -1 },
        enableAccessInstructions: { type: Boolean, default: false },
    },
    emits: ['hoverStart', 'hoverEnd', 'openWaypointDetails', 'deleteWaypoint'],
    setup(props) {
        const store = getStore();
        const portalStore = usePortalStore();
        const { viewAs } = portalStore;
        const waypointLetterClassExtension = computed(() => {
            var _a;
            if (!props.waypoint)
                return;
            return (_a = portalStore.waypoints.get(props.waypoint.id)) === null || _a === void 0 ? void 0 : _a.computed.waypointLetterClassExtension;
        });
        const baseClassName = 'ItineraryWaypoint__letter-container';
        const waypointLetterClasses = computed(() => waypointLetterClassExtension
            ? [baseClassName, `${baseClassName}${waypointLetterClassExtension.value}`]
            : [baseClassName]);
        const formattedAddress = computed(() => {
            if (!props.waypoint)
                return [];
            return formatAddressUS(props.waypoint.location, undefined, AddressFormatType.MULTI_LINE);
        });
        const additionalInstructions = computed(() => {
            var _a;
            return (props.waypoint && props.enableAccessInstructions)
                ? (_a = props.waypoint.location.additionalInstructions) !== null && _a !== void 0 ? _a : null
                : null;
        });
        const additionalInstructionsOpen = ref(false);
        const classNames = computed(() => {
            const classes = [];
            if (additionalInstructions.value) {
                classes.push('ItineraryWaypointLocationAndDurationCard__has-additional-instructions');
            }
            return classes;
        });
        const formattedInfo = computed(() => {
            var _a;
            if (!props.waypoint)
                return;
            const date = (_a = portalStore.waypoints.getRaw(props.waypoint.id)) === null || _a === void 0 ? void 0 : _a.date;
            if (!date || isNullish(date === null || date === void 0 ? void 0 : date.timestamp))
                return;
            const time = dayjs(date.timestamp).tz(date.timeZone).format('h:mm a');
            const tz = dayjs(date.timestamp).tz(date.timeZone).format('z');
            const timeZone = date.timeZone !== dayjs.tz.guess() ? tz : '';
            return {
                title: time,
                decorator: timeZone,
            };
        });
        const waypointLetter = computed(() => props.waypointIndex >= 0 ? getWaypointLetter(props.waypointIndex) : '');
        const totalWaypointDuration = computed(() => {
            var _a, _b, _c;
            return ((_a = props.waypoint) === null || _a === void 0 ? void 0 : _a.id)
                ? (_c = (_b = portalStore.waypoints.get(props.waypoint.id)) === null || _b === void 0 ? void 0 : _b.computed.totalDuration) !== null && _c !== void 0 ? _c : 0 : 0;
        });
        const waypointDuration = ref(totalWaypointDuration.value);
        const setWaypointDuration = (event) => {
            if (props.waypointIndex < 0)
                return;
            const value = parseInt(event.target.value, 10);
            if (typeof value === 'number' && !Number.isNaN(value))
                waypointDuration.value = value;
        };
        const setWaypointActionDuration = () => {
            if (props.waypointIndex < 0 || !props.waypoint)
                return;
            const duration = waypointDuration.value;
            if (!(duration > 0 && duration !== totalWaypointDuration.value))
                return;
            setWaypointTotalDuration(props.waypoint.id, duration);
            waypointStartTimes({ fix: true });
        };
        const placeGroupPicture = computed(() => { var _a, _b, _c; return (_c = (_b = store.getters['places/getPlaceGroupByPlaceID']((_a = props.waypoint) === null || _a === void 0 ? void 0 : _a.placeID)) === null || _b === void 0 ? void 0 : _b.picture) !== null && _c !== void 0 ? _c : null; });
        const placeGroupName = computed(() => { var _a, _b, _c; return (_c = (_b = store.getters['places/getPlaceGroupByPlaceID']((_a = props.waypoint) === null || _a === void 0 ? void 0 : _a.placeID)) === null || _b === void 0 ? void 0 : _b.name) !== null && _c !== void 0 ? _c : null; });
        return {
            additionalInstructions,
            additionalInstructionsOpen,
            classNames,
            formattedAddress,
            preventParentClickAndDefault,
            formattedInfo,
            waypointLetterClasses,
            waypointLetterClassExtension,
            placeGroupPicture,
            placeGroupName,
            setWaypointActionDuration,
            setWaypointDuration,
            totalWaypointDuration,
            waypointLetter,
            viewAs,
        };
    },
});

export { script as default };
